import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Drawer, IconButton, LinearProgress, Typography } from "@mui/material";
import { Close, Menu } from "@mui/icons-material";
import useInterfaceConfig from "@hooks/useInterfaceConfigV2";
import useIsAuthenticated from "@hooks/useIsAuthenticated";
import { getStaticResourceUrl } from "@helpers/funciones";
import { PATHS_AUTH_V2 } from "@helpers/constantes";
import { TofuSidebarContent } from "./TofuSidebar";
import { SIDEBAR_WIDTH, getSidebarColors, loginButtonSx, useTofuRail, white } from "./tofuSidebar.helpers";

/** Alto del logo en el navbar: más chico que en el sidebar para no comerse la pantalla. */
const LOGO_HEIGHT = 28;

/**
 * Navbar del wizard v2 para móvil (reemplaza al sidebar debajo de `md`): barra fija con el mismo
 * fondo oscuro del sidebar, el logo, "Iniciar sesión" si no hay sesión y el botón de menú; abajo,
 * la fase actual con su avance. El menú abre el contenido completo del sidebar en un `Drawer`.
 */
export const NavbarTofuMobile = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const interfaceConfig = useInterfaceConfig();
  const isAuthenticated = useIsAuthenticated();
  // Por ahora el avance es por etapa (Onboarding, Expediente, Firma), igual que el sidebar.
  const { activeGroup, activeGroupIndex, groups, groupProgress } = useTofuRail();
  const [open, setOpen] = useState(false);

  // El menú se cierra al cambiar de pantalla.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const logo = getStaticResourceUrl(interfaceConfig?.lateral?.logo_bg_dark || interfaceConfig?.navbar.logo);

  return (
    <>
      <Box
        component="header"
        sx={{
          display: { xs: "block", md: "none" },
          position: "sticky",
          top: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          width: "100%",
          boxSizing: "border-box",
          bgcolor: (theme) => getSidebarColors(theme).bg,
          boxShadow: "0 2px 8px rgba(0,0,0,.18)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, px: 2, py: 1.25 }}>
          <Box
            component="img"
            src={logo}
            alt="logo"
            sx={{
              height: LOGO_HEIGHT,
              width: "auto",
              maxWidth: "45%",
              objectFit: "contain",
              flexShrink: 1,
              minWidth: 0,
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            {!isAuthenticated && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(PATHS_AUTH_V2.login.path)}
                data-testid="navbar-mobile-login-button"
                sx={[loginButtonSx, { height: 32, px: 1.5, fontSize: 12.5, whiteSpace: "nowrap" }]}
              >
                Iniciar sesión
              </Button>
            )}
            <IconButton
              aria-label="Abrir menú"
              onClick={() => setOpen(true)}
              data-testid="navbar-mobile-menu-button"
              sx={{ color: "#fff", "&:hover": { bgcolor: white(0.08) } }}
            >
              <Menu />
            </IconButton>
          </Box>
        </Box>

        {activeGroup && (
          <Box sx={{ px: 2, pb: 1.25 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1, mb: 0.75 }}>
              <Typography noWrap sx={{ fontSize: 12, fontWeight: 600, color: "#fff" }}>
                {activeGroup.label}
              </Typography>
              <Typography
                sx={{ fontSize: 11, color: white(0.48), fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
              >
                Paso {activeGroupIndex + 1} de {groups.length}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={groupProgress}
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: white(0.12),
                "& .MuiLinearProgress-bar": { borderRadius: 2, bgcolor: (theme) => getSidebarColors(theme).dot },
              }}
            />
          </Box>
        )}
      </Box>

      <Drawer
        anchor="left"
        open={open}
        onClose={() => setOpen(false)}
        sx={{ display: { md: "none" } }}
        PaperProps={{
          sx: {
            width: SIDEBAR_WIDTH,
            maxWidth: "85vw",
            display: "flex",
            flexDirection: "column",
            bgcolor: (theme) => getSidebarColors(theme).bg,
            borderRight: "none",
          },
        }}
      >
        <IconButton
          aria-label="Cerrar menú"
          onClick={() => setOpen(false)}
          data-testid="navbar-mobile-close-button"
          sx={{
            position: "absolute",
            top: 18,
            right: 10,
            zIndex: 1,
            color: white(0.6),
            "&:hover": { bgcolor: white(0.08) },
          }}
        >
          <Close fontSize="small" />
        </IconButton>
        <TofuSidebarContent />
      </Drawer>
    </>
  );
};
